import { makeAutoObservable, runInAction } from "mobx";
import apiHandler from "../handlers/ApiHandler";
import {
  subscribeToWarnings,
  unsubscribeFromWarnings,
} from "../handlers/WarningWebsocketHandler";
import authStore from "./AuthStore";

class WarningStore {
  warningsMap = new Map();
  readIds = new Set();
  readersMap = new Map();
  connected = false;
  connectionStatus = "Disconnected";

  constructor() {
    makeAutoObservable(this);
  }

  async loadWarnings() {
    try {
      const res = await apiHandler.get("/api/warnings");
      console.log("✅ Warnings API response:", res);

      if (!Array.isArray(res)) {
        console.error("🚨 loadWarnings() expected array but got:", res);
        return;
      }

      runInAction(() => {
        this.warningsMap.clear();
        res.forEach((w) => this.warningsMap.set(w._id, w));
      });
    } catch (err) {
      console.error("❌ Failed to load warnings:", err);
    }
  }

  async loadReadWarningIds() {
    try {
      const userId = authStore.user?.id || authStore.user?.userID;
      if (!userId) throw new Error("Missing user ID");

      const res = await apiHandler.post("/api/warnings/read-ids", {
        user_id: userId,
      });

      runInAction(() => {
        this.readIds = new Set(res.data.read_warning_ids);
      });
    } catch (err) {
      console.error("Failed to load read warning IDs:", err);
    }
  }

  async loadReaders(warningId) {
    try {
      const res = await apiHandler.get(`/api/warnings/${warningId}/readers`);
      runInAction(() => {
        this.readersMap.set(warningId, res.users);
      });
    } catch (err) {
      console.error("Failed to load readers:", err);
    }
  }

  markAsRead(id) {
    const userId = authStore.user?.id;
    if (!userId) return;

    runInAction(() => {
      this.readIds.add(id);
    });

    apiHandler
      .post("/api/warnings/mark-read", {
        user_id: userId,
        warning_ids: [id],
        mode: "read",
      })
      .catch(() => {
        console.error("Failed to mark warning as read");
        runInAction(() => {
          this.readIds.delete(id);
        });
      });
  }

  markAsUnread(id) {
    const userId = authStore.user?.id;
    if (!userId) return;

    runInAction(() => {
      this.readIds.delete(id);
    });

    apiHandler
      .post("/api/warnings/mark-read", {
        user_id: userId,
        warning_ids: [id],
        mode: "unread",
      })
      .catch(() => {
        console.error("Failed to mark warning as unread");
        runInAction(() => {
          this.readIds.add(id);
        });
      });
  }

  async createWarning({ field, likelihood, message }) {
    try {
      const userId = authStore.user?.id;
      if (!userId) throw new Error("User ID missing from authStore");

      const res = await apiHandler.post("/api/warnings", {
        field,
        likelihood,
        message,
        user_id: userId,
      });

      if (res && res.data && res.data._id) {
        runInAction(() => {
          this.warningsMap.set(res.data._id, res.data);
        });
      } else {
        console.error(
          "Warning creation failed: Missing _id in response",
          res?.data || res
        );
      }
    } catch (err) {
      console.error("Failed to create warning:", err);
      throw err;
    }
  }

  get warningsList() {
    return Array.from(this.warningsMap.values()).sort(
      (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
    );
  }

  get unreadWarningIds() {
    return this.warningsList
      .filter((w) => !this.readIds.has(w._id))
      .map((w) => w._id);
  }

  get unreadCount() {
    return Array.from(this.warningsMap.values()).filter(
      (w) => !this.readIds.has(w._id)
    ).length;
  }

  startRealtime() {
    if (!this.connected) {
      this.connectionStatus = "Connecting";
      subscribeToWarnings(this.addNewWarning, (status) => {
        runInAction(() => {
          this.connectionStatus = status;
        });
      });
      this.connected = true;
    }
  }

  stopRealtime() {
    if (this.connected) {
      unsubscribeFromWarnings(this.addNewWarning);
      this.connected = false;
      this.connectionStatus = "Disconnected";
    }
  }

  addNewWarning = (warning) => {
    if (!warning || !warning._id) {
      console.error("Invalid warning payload from WebSocket", warning);
      return;
    }

    runInAction(() => {
      this.warningsMap.set(warning._id, warning);
    });
  };
}

const warningStore = new WarningStore();
export default warningStore;
